import { useMemo } from "react";
import { AlertTriangle, CheckCircle, Info } from "lucide-react";

interface FeeComparisonIndicatorProps {
  actualFee: number | null | undefined; // Amount in cents
  minimumFee: number | null | undefined; // Amount in cents  
  preferredFee: number | null | undefined; // Amount in cents
}

export function FeeComparisonIndicator({ 
  actualFee, 
  minimumFee, 
  preferredFee 
}: FeeComparisonIndicatorProps) {
  const comparison = useMemo(() => {
    // Return null if no actual fee to compare
    if (!actualFee || actualFee <= 0) return null;
    
    const minimum = minimumFee || 0;
    const preferred = preferredFee || 0;
    
    // If no preferences are set, don't show any indicator
    if (!minimum && !preferred) return null;
    
    if (actualFee < minimum) {
      return {
        status: 'below-minimum',
        message: `Below minimum (£${(minimum / 100).toFixed(2)})`,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        icon: AlertTriangle
      };
    } else if (actualFee < preferred) {
      return {
        status: 'below-preferred',
        message: `Below preferred (£${(preferred / 100).toFixed(2)})`,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        icon: Info
      };
    } else {
      return {
        status: 'meets-preferred',
        message: 'Meets preferred rate',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        icon: CheckCircle
      };
    }
  }, [actualFee, minimumFee, preferredFee]);

  if (!comparison) return null;

  return (
    <div className={`mt-2 px-2 py-1 rounded-md flex items-center gap-2 ${comparison.bgColor} ${comparison.borderColor} border`}>
      <comparison.icon className={`h-4 w-4 ${comparison.color}`} />
      <span className={`text-sm ${comparison.color}`}>
        {comparison.message}
      </span>
    </div>
  );
}